import {
  Component,
  ChangeDetectionStrategy,
  NgZone,
  ElementRef,
  ViewEncapsulation,
  OnDestroy,
  AfterViewChecked,
  input,
  AfterViewInit,
  viewChild,
  inject,
} from '@angular/core';
import * as L from 'leaflet';
import { ImageOverlay } from 'leaflet';
import { explicitEffect } from 'ngxtension/explicit-effect';
import { LazyStylesService } from '@cue/lazy-styles';
import { loadNavigationLeafletStyles } from '@cue/navigation-widgets';

@Component({
  selector: 'addin-resource-map',
  templateUrl: './resource-map.component.html',
  styleUrls: ['./resource-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [],
})
export class ResourceMapComponent implements AfterViewChecked, OnDestroy, AfterViewInit {
  private lazyStyles = inject(LazyStylesService);

  breadcrumbContainer = viewChild('breadcrumbContainer', { read: ElementRef });

  resourceId = input();

  geoData = input<string>();

  imageSrc = input<string>();
  setImageMapFromInput = explicitEffect([this.imageSrc], ([imageSrc]) => {
    void this.setImageMap(imageSrc);
  });

  mapElement = viewChild('mapElement', { read: ElementRef });
  private map?: L.Map;
  private imageOverlay?: ImageOverlay;
  private renderedLayers = new L.FeatureGroup([], {});

  constructor(private zone: NgZone) {}

  async ngAfterViewInit(): Promise<void> {
    await loadNavigationLeafletStyles(this.lazyStyles);
    this.initMap();
    this.createNewLayer(this.geoData());
  }

  ngAfterViewChecked() {
    if (this.breadcrumbContainer()) {
      this.breadcrumbContainer()!.nativeElement.scrollLeft = this.breadcrumbContainer()!.nativeElement.scrollWidth;
    }
  }

  private createNewLayer(areaGeoData: string) {
    if (!this.map || !areaGeoData) {
      return;
    }

    let geoJson: any;
    try {
      geoJson = JSON.parse(areaGeoData);
    } catch {
      return;
    }

    if (geoJson.properties == null) {
      geoJson.properties = {};
    }
    // if (areaGeoData.typeId == 2) {
    //
    //   geoJson.properties.resourceId = areaGeoData.resourceId;
    // }
    const geometryType = geoJson.type;

    const geo = L.geoJSON(geoJson, {
      style: {
        color: 'orange',
        fill: true,
        fillOpacity: 0.6,
        fillColor: 'orange',
      },
      pointToLayer: (feature, latlng) => {
        // if (areaGeoData.radius) {
        //   return new L.Circle(latlng, areaGeoData.radius);
        // } else {
        //   return new L.Marker(latlng, {});
        // }
        return new L.Marker(latlng, {});
      },
    });
    geo.addTo(this.renderedLayers);
    const bounds = geo.getBounds();
    if (bounds.isValid()) {
      this.map.fitBounds(bounds.pad(5), {
        animate: false,
      });
    }
  }

  private paintLayers(geoData) {
    if (geoData.loading) return;
    const existingLayers = this.renderedLayers
      .getLayers()
      .filter((x: any) => {
        if (x.getLayers) {
          return x.getLayers()[0];
        }
        return false;
      })
      .map((x: any) => x.getLayers()[0]);

    geoData.data.forEach((assistGeoData) => {
      if (assistGeoData.resourceId === this.resourceId()) {
        this.createNewLayer(assistGeoData);
      }
    });
  }

  private prepareMapAndLayers() {
    const mapElement = this.mapElement()?.nativeElement;
    if (!mapElement || this.map) {
      return;
    }

    this.zone.runOutsideAngular(() => {
      this.map = L.map(mapElement, {
        preferCanvas: false,
        center: [0, 0],
        attributionControl: false,
        zoom: 1,
        minZoom: -1,
        maxZoom: 8,
        crs: L.CRS.Simple,
      } as any) as any;
    });

    this.map.addLayer(this.renderedLayers);
  }

  private getImageMetadata(imageSrc: string): Promise<{ width: number; height: number }> {
    return new Promise((resolve) => {
      const img = new Image();
      img.addEventListener('load', function () {
        resolve({
          width: this.naturalWidth,
          height: this.naturalHeight,
        });
      });
      img.src = imageSrc;
    });
  }

  private initMap() {
    this.prepareMapAndLayers();
    if (this.imageSrc()) {
      return this.setImageMap(this.imageSrc());
    }
    this.map?.invalidateSize();
  }

  private async setImageMap(imageSrc: string) {
    if (!imageSrc || !this.map) {
      return;
    }

    const imageMetadata = await this.getImageMetadata(imageSrc);
    if (!this.map) {
      return;
    }

    const width = imageMetadata.width;
    const height = imageMetadata.height;
    const southWest = this.map.unproject([0, height], 2);
    const northEast = this.map.unproject([width, 0], 2);
    const bounds = new L.LatLngBounds(southWest, northEast);
    if (this.imageOverlay) {
      this.imageOverlay.setUrl(imageSrc);
      this.imageOverlay.setBounds(bounds);
    } else {
      this.imageOverlay = L.imageOverlay(imageSrc, bounds, {
        zIndex: -1,
      }).addTo(this.map);
    }

    this.map.fitBounds(this.imageOverlay.getBounds(), {
      animate: false,
    });
    this.map.invalidateSize();
  }

  ngOnDestroy(): void {
    this.map?.remove();
    this.map = undefined;
  }
}
